import { AssetSubClassName } from '@api/api/entity-constants';
import { MappedEntity, PrimaryMethod } from '@api/api/entity-schema';
import { PreliminaryPrice } from '@api/api/price-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import { PriceDisplay } from '@design/widgets/price-display/PriceDisplay';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LivePriceInfo } from '../../hooks/use-live-price';
import { globalPriceFormatter } from '../../utils/asset-price/assetPrice';
import {
  formatPriceDisplay,
  getPrimaryObservationLabel,
  getPrimaryObservationUnit,
  isPrimaryObservation,
} from './helpers/price-display-helpers';
import styles from './PriceDisplayView.module.scss';

export interface PriceDisplayViewProps {
  entity: MappedEntity;
  suffix?: string;
  livePriceInfo?: LivePriceInfo;
  snakeMeta?: SnakeMeta;
  preliminaryPrice?: PreliminaryPrice;
  isChartHovered: boolean;
  isCondensed?: boolean;
}

const QuoteDisplay = ({
  entity,
  livePriceInfo,
  snakeMeta,
  suffix,
  preliminaryPrice,
  isChartHovered,
  isCondensed = false,
}: PriceDisplayViewProps) => {
  const { t } = useTranslation(['analyze']);
  const isObservation = isPrimaryObservation(entity);
  const unit = isObservation
    ? getPrimaryObservationUnit(entity, snakeMeta)
    : entity.currency;

  const priceFormatter = useCallback(
    (price: number) =>
      globalPriceFormatter({
        entity,
        price,
        priceSuffix: isObservation ? '' : (suffix ?? ''),
        shouldShowCurrency: false,
      }),
    [suffix, entity, isObservation]
  );

  if (
    !isObservation &&
    ((!livePriceInfo && snakeMeta?.last_value === undefined) ||
      !snakeMeta?.last_timestamp)
  ) {
    return null;
  }

  const price = formatPriceDisplay({
    exchange: entity.exchange.code,
    livePriceInfo,
    preliminaryPrice,
    meta: snakeMeta,
    isHovered: isChartHovered,
    t,
    isObservation,
  });

  const hasLivePrice =
    price.secondary?.price !== undefined && !!price.secondary?.isLive;
  const isPrimaryPriceCondensed = isCondensed && hasLivePrice;
  const showMarketIndicator = isObservation || !isCondensed;

  return (
    <div className={styles.container} data-testid="price-display-view">
      {!isPrimaryPriceCondensed && (
        <div className={styles.primary} data-observation={isObservation}>
          {isObservation && (
            <div className={styles.dateSpan}>
              {getPrimaryObservationLabel(entity, snakeMeta)}
            </div>
          )}
          {price.primary.price !== undefined && (
            <PriceDisplay
              className={styles.styledPriceDisplay}
              price={priceFormatter(price.primary.price)}
              currency={unit}
              priceChange={price.primary.priceChange}
              liveLabel={
                price.primary?.isLive
                  ? t('analyze:analysis.livePrice')
                  : undefined
              }
              timeNode={
                !price.primary.isLive && (
                  <span className={styles.dateSpan}>
                    {price.primary.asOfDate
                      ? t('analyze:analysis.asOfDate', {
                          date: price.primary.asOfDate,
                        })
                      : t('analyze:analysis.asOfLastClose')}
                  </span>
                )
              }
              showMarketIndicator={showMarketIndicator}
            />
          )}
        </div>
      )}
      {!isChartHovered &&
        price.secondary !== undefined &&
        !!price.secondary.price &&
        !!price.secondary.isLive && (
          <PriceDisplay
            className={styles.styledPriceDisplay}
            data-testid="secondary-price"
            price={priceFormatter(price.secondary.price)}
            currency={unit}
            priceChange={price.secondary.priceChange}
            liveLabel={t('analyze:analysis.livePrice')}
            outsideHoursText={price.secondary.outsideHours}
            isPrimary={false}
            showMarketIndicator={showMarketIndicator}
          />
        )}
    </div>
  );
};

const EconomicSeriesTitle = ({ entity, snakeMeta }: PriceDisplayViewProps) => {
  const { t } = useTranslation('analyze');
  return (
    <div className={styles.countrySeries} data-testid="price-display-view">
      <span>
        {t('analyze:analysis.chartSeries', {
          series: getPrimaryObservationLabel(entity, snakeMeta),
        })}
      </span>
    </div>
  );
};

export const PriceDisplayView = (props: PriceDisplayViewProps) => {
  const isCountryOrArea = [
    AssetSubClassName.EcoCountry,
    AssetSubClassName.EcoArea,
  ].some(subClass => subClass === props.entity.sub_class);
  if (
    isCountryOrArea &&
    props.entity.primary_method !== PrimaryMethod.GdpLevel
  ) {
    return null;
  }
  return isCountryOrArea ? (
    <EconomicSeriesTitle {...props} />
  ) : (
    <QuoteDisplay {...props} />
  );
};
